<template>
  <div class="main_profile_wrapper">
    <div v-if="loading" class="text-center p-5">
      <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
    </div>

    <div v-else-if="studentNotebook" class="details_container">
      <div class="details_header no-print">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <el-tag size="small" type="info" v-if="studentNotebook.school_grade">
                <i class="fa fa-graduation-cap mr-1"></i> {{ localization('Grade') }} {{ studentNotebook.school_grade }}
              </el-tag>
              <el-tag size="small" type="warning" v-if="getLanguageName(studentNotebook)">
                <i class="fa fa-globe mr-1"></i> {{ localization('Language') }}: {{ getLanguageName(studentNotebook) }}
              </el-tag>
              <el-tag size="small" type="primary" v-if="studentNotebook.topic_card_name">
                <i class="fa fa-bookmark mr-1"></i> {{ localization('Topic') }}: {{ studentNotebook.topic_card_name }}
              </el-tag>
              <el-tag size="small" type="success" v-if="studentNotebook.sub_topic_card_name">
                <i class="fa fa-list mr-1"></i> {{ localization('Subtopic') }}: {{ studentNotebook.sub_topic_card_name }}
              </el-tag>
            </div>
            <h1 class="title font-weight-bold">{{ studentNotebook.name }}</h1>
          </div>
          <div class="d-flex align-items-center gap-2">
            <el-button type="info" @click="printNotebook">
              <i class="fa fa-print mr-2"></i> {{ localization('Print') }}
            </el-button>
            <el-button type="primary" @click="$router.back()">
              <i class="fa fa-arrow-left mr-2"></i> {{ localization('Back') }}
            </el-button>
          </div>
        </div>
      </div>

      <div class="notebook_content_section mt-4">
        <div
          v-if="studentNotebook.body || studentNotebook.student_notebook"
          class="notebook_body_card p-4 bg-white border rounded shadow-sm"
          v-html="studentNotebook.body || studentNotebook.student_notebook"
        ></div>

        <div v-else class="text-center p-5 border rounded bg-light">
          <p class="text-muted">{{ localization('No content found for this notebook.') }}</p>
        </div>
      </div>
    </div>

    <div v-else class="text-center p-5">
      <el-alert
        :title="localization('Error')"
        type="error"
        :description="localization('Notebook not found or failed to load.')"
        show-icon
      >
      </el-alert>
      <el-button class="mt-4" type="primary" @click="$router.back()">
        {{ localization('Go Back') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "StudentsNotebookDetails",
  data() {
    return {
      languageOptions: []
    };
  },
  computed: {
    ...mapGetters("students_notebook", [
      "studentNotebook",
      "loading"
    ]),
    computedLanguageOptions() {
      if (this.languageOptions && this.languageOptions.length) {
        return this.languageOptions;
      }
      const all = this.$store.getters["language/allLanguages"];
      if (all && all.length) return this.normalizeLanguages(all);
      const langs = this.$store.getters["language/languages"];
      if (langs && langs.length) return this.normalizeLanguages(langs);
      return [];
    }
  },
  methods: {
    normalizeLanguages(response) {
      if (Array.isArray(response)) return response;
      if (!response || typeof response !== "object") return [];
      if (Array.isArray(response.languages)) return response.languages;
      if (response.data && Array.isArray(response.data.languages)) return response.data.languages;
      if (response.data && Array.isArray(response.data)) return response.data;
      return [];
    },
    getLanguageName(notebook) {
      if (!notebook) return "";
      if (notebook.language_name && typeof notebook.language_name === "string" && isNaN(notebook.language_name)) {
        return notebook.language_name;
      }
      if (notebook.language && typeof notebook.language === "string" && isNaN(notebook.language)) {
        return notebook.language;
      }
      if (notebook.language && typeof notebook.language === "object") {
        const label = notebook.language.title || notebook.language.name || notebook.language.label || notebook.language.language;
        if (label) return label;
      }

      const langId = notebook.language_id != null && notebook.language_id !== "" 
        ? notebook.language_id 
        : (notebook.language != null && notebook.language !== "" ? notebook.language : null);

      if (langId != null) {
        const options = this.computedLanguageOptions;
        if (options && options.length) {
          const found = options.find(l => String(l.id) === String(langId) || String(l.title).toLowerCase() === String(langId).toLowerCase());
          if (found) {
            return found.title || found.name || found.label || found.language || "";
          }
        }
        if (isNaN(langId)) {
          return String(langId);
        }
      }
      return "";
    },
    async fetchLookups() {
      let options = [];
      try {
        const langsResponse = await this.$store.dispatch("language/allLanguages");
        options = this.normalizeLanguages(langsResponse);
      } catch (e) {
        console.warn("allLanguages error in details:", e);
      }

      if (!options || !options.length) {
        try {
          await this.$store.dispatch("language/getLanguages");
          const fallbackLangs = this.$store.getters["language/languages"];
          options = this.normalizeLanguages(fallbackLangs);
        } catch (e) {
          console.error("Fallback getLanguages error in details:", e);
        }
      }
      this.languageOptions = options || [];
    },
    fetchDetails() {
      const id = this.$route.params.id;
      if (id) {
        this.$store.dispatch("students_notebook/ShowStudentsNotebook", id);
      }
    },
    printNotebook() {
      window.print();
    }
  },
  mounted() {
    this.fetchLookups();
    this.fetchDetails();
  }
};
</script>

<style lang="scss" scoped>
.main_profile_wrapper {
  width: 100%;
  max-width: 100%;
  padding: 10px 30px 60px;
  min-height: 100vh;
}

.details_container {
  max-width: 1000px;
  margin: 0 auto;
}

.details_header {
  border-bottom: 1px solid #eaecf0;
  padding-bottom: 20px;

  .title {
    font-size: 1.7rem;
    color: #101828;
    margin-bottom: 8px;
  }
}

.gap-2 {
  gap: 10px;
}

.notebook_body_card {
  line-height: 1.6;
  color: #344054;
  border-radius: 12px;
  border: 1px solid #eaecf0;

  ::v-deep {
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }
    ul,
    ol {
      padding-left: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
      th,
      td {
        border: 1px solid #d0d5dd;
        padding: 8px 12px;
      }
    }
  }
}

@media print {
  .no-print,
  .sidebar,
  .navbar,
  .main-panel > .navbar,
  .footer {
    display: none !important;
  }
  .main-panel {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .main_profile_wrapper {
    padding: 0 !important;
  }
  .notebook_body_card {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
</style>
